/**
 * M66 — maintenance-mode global state.
 *
 * The switch lives in `system_configs.maintenance_mode` (default false). When it
 * is ON, writes are blocked app-wide for everyone EXCEPT admins, and a notice
 * banner is shown on every screen. Reads always continue untouched.
 *
 * This module is intentionally framework-free: the data-layer guard (the
 * supabase fetch wrapper in `@/lib/supabase/client`) runs OUTSIDE React, so it
 * needs a synchronous, importable flag. The React side (MaintenanceProvider)
 * keeps the flag in sync and also drives the banner/notice UI.
 *
 * When maintenance is OFF (the default) every function here is a no-op for
 * writes, so the app behaves EXACTLY as before.
 */
type Listener = () => void;

interface MaintenanceState {
  active: boolean;
  // true when the current viewer is an admin (system admin or role admin) —
  // admins are exempt from write blocking.
  adminExempt: boolean;
  // timestamp of the most recent blocked write attempt; used to briefly
  // suppress the generic "save failed" toast that would otherwise contradict
  // the dedicated maintenance notice.
  lastBlockedAt: number;
}

/** How long after a blocked write the generic error toast is suppressed. */
const BLOCKED_NOTICE_WINDOW_MS = 4000;

const state: MaintenanceState = { active: false, adminExempt: false, lastBlockedAt: 0 };
const listeners = new Set<Listener>();

function emit(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // A misbehaving subscriber must never break the state update.
    }
  });
}

/** Set by useMaintenanceMode() after the config read resolves. */
export function setMaintenanceActive(active: boolean): void {
  if (state.active === active) return;
  state.active = active;
  emit();
}

/** Set by MaintenanceProvider from the signed-in profile. */
export function setMaintenanceAdminExempt(exempt: boolean): void {
  if (state.adminExempt === exempt) return;
  state.adminExempt = exempt;
  emit();
}

/** True whenever maintenance mode is on (regardless of viewer). */
export function isMaintenanceActive(): boolean {
  return state.active;
}

/**
 * True when a write from the current viewer must be blocked: maintenance is ON
 * and the viewer is NOT an admin. This is the single source of truth the
 * data-layer guard consults.
 */
export function isMaintenanceBlocking(): boolean {
  return state.active && !state.adminExempt;
}

/** Record a blocked write attempt, dispatch the UI notice, brief suppression window. */
export function markMaintenanceBlocked(): void {
  state.lastBlockedAt = Date.now();
  emit();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('elkaway:maintenance-blocked'));
  }
}

/** True for a few seconds after a blocked write — toast suppression window. */
export function isRecentlyMaintenanceBlocked(): boolean {
  return state.lastBlockedAt > 0 && Date.now() - state.lastBlockedAt < BLOCKED_NOTICE_WINDOW_MS;
}

/** Subscribe to state changes (banner/notice re-render). Returns an unsubscribe fn. */
export function subscribeMaintenance(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Thrown by the data-layer guard when a write is rejected by maintenance mode.
 * Carries an HTTP-ish status + a stable code so callers (and the toast layer)
 * can recognize it.
 */
export class MaintenanceBlockedError extends Error {
  status = 503;

  code = 'maintenance_mode';

  constructor() {
    super('MAINTENANCE_MODE');
    this.name = 'MaintenanceBlockedError';
  }
}